import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { getInitials } from '../utils';

export const cx = (...parts) => parts.filter(Boolean).join(' ');

const BUTTON_BASE = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const BUTTON_SIZE = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-3.5 text-sm',
  lg: 'h-11 px-5 text-sm',
};
// Primary is near-black like the references; swap this one line for an
// orange primary (bg-peach-400 text-gray-900 hover:bg-peach-500) if preferred.
const BUTTON_VARIANT = {
  primary: 'bg-gray-900 text-white hover:bg-gray-700',
  secondary: 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-50',
  ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  danger: 'bg-white text-red-600 border border-red-200 hover:bg-red-50',
  accent: 'bg-lavender-500 text-white hover:bg-lavender-600',
};

export const Button = React.forwardRef(function Button({ variant = 'primary', size = 'md', className, type = 'button', ...props }, ref) {
  return <button ref={ref} type={type} {...props} className={cx(BUTTON_BASE, BUTTON_SIZE[size], BUTTON_VARIANT[variant], className)} />;
});

export function IconButton({ label, className, size = 16, icon: Icon, tone = 'default', ...props }) {
  const tones = {
    default: 'text-gray-400 hover:text-gray-900 hover:bg-gray-100',
    danger: 'text-gray-400 hover:text-red-600 hover:bg-red-50',
  };
  return (
    <button type="button" aria-label={label} title={label} {...props} className={cx('inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors', tones[tone], className)}>
      <Icon size={size} />
    </button>
  );
}

export const FIELD = 'w-full h-9 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:text-gray-500';

export const Input = React.forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} {...props} className={cx(FIELD, className)} />;
});

export const Select = React.forwardRef(function Select({ className, ...props }, ref) {
  return <select ref={ref} {...props} className={cx(FIELD, className)} />;
});

export const Textarea = React.forwardRef(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} {...props} className={cx(FIELD, 'h-auto py-2 resize-y', className)} />;
});

export function Field({ label, htmlFor, help, required, children, className }) {
  return (
    <div className={className}>
      {label && (
        <div className="flex items-center gap-1 mb-1.5">
          <label htmlFor={htmlFor} className="block text-xs font-medium text-gray-600">{label}</label>
          {required && <span aria-hidden="true" className="text-xs text-gray-400">*</span>}
        </div>
      )}
      {children}
      {help && <p className="mt-1.5 text-xs text-gray-400">{help}</p>}
    </div>
  );
}

export function Card({ className, children, ...props }) {
  return <div {...props} className={cx('bg-white border border-gray-200 rounded-xl', className)}>{children}</div>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

export function SectionTitle({ children, actions, className }) {
  return (
    <div className={cx('flex items-center justify-between gap-3 mb-3', className)}>
      <h2 className="text-base font-semibold text-gray-900">{children}</h2>
      {actions}
    </div>
  );
}

export function Label({ children, className }) {
  return <div className={cx('text-[11px] font-semibold uppercase tracking-wider text-gray-500', className)}>{children}</div>;
}

const BADGE_TONE = {
  gray: 'bg-gray-100 text-gray-700',
  purple: 'bg-lavender-100 text-lavender-600',
  orange: 'bg-peach-100 text-peach-600',
  green: 'bg-verde-100 text-verde-600',
  blue: 'bg-azul-100 text-azul-600',
  yellow: 'bg-amarelo-100 text-amarelo-600',
  red: 'bg-red-100 text-red-700',
};

export function Badge({ tone = 'gray', className, children }) {
  return <span className={cx('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider leading-4', BADGE_TONE[tone], className)}>{children}</span>;
}

// Segmented control: options are [value, label]. Pass role="tablist" when it
// switches panels.
export function Segmented({ value, onChange, options, size = 'sm', className, role }) {
  const padding = size === 'sm' ? 'px-2.5 h-7 text-xs' : 'px-3 h-8 text-sm';
  return (
    <div role={role} className={cx('inline-flex max-w-full gap-0.5 bg-gray-100 p-0.5 rounded-lg overflow-x-auto', className)}>
      {options.map(([val, label]) => {
        const active = val === value;
        return (
          <button
            key={val}
            type="button"
            role={role === 'tablist' ? 'tab' : undefined}
            aria-selected={role === 'tablist' ? active : undefined}
            aria-pressed={role === 'tablist' ? undefined : active}
            onClick={() => onChange(val)}
            className={cx('rounded-md font-medium whitespace-nowrap transition-colors', padding, active ? 'bg-white text-gray-900 shadow-soft' : 'text-gray-500 hover:text-gray-900')}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

const AVATAR_SIZE = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-16 h-16 text-xl',
  xl: 'w-24 h-24 text-3xl',
};

// Photo when there is one, otherwise initials on the member's color.
export function Avatar({ src, name, color = '#737373', size = 'md', className, square = false }) {
  const shape = square ? 'rounded-lg' : 'rounded-full';
  const base = cx('inline-flex items-center justify-center flex-shrink-0 overflow-hidden font-semibold text-white select-none', shape, AVATAR_SIZE[size], className);
  if (src) return <img src={src} alt={name || ''} className={cx(base, 'object-cover bg-gray-100')} />;
  return <span aria-hidden="true" className={base} style={{ backgroundColor: color }}>{getInitials(name)}</span>;
}

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div className="text-center py-12 px-4 border border-dashed border-gray-200 rounded-xl">
      {Icon && <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center mx-auto mb-3"><Icon size={18} /></div>}
      <p className="text-sm font-medium text-gray-800">{title}</p>
      {body && <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Notice({ tone = 'info', children, className, action }) {
  const tones = {
    info: 'bg-lavender-50 border-lavender-100 text-gray-700',
    success: 'bg-verde-100 border-verde-200 text-verde-600',
    warning: 'bg-amarelo-100 border-amarelo-200 text-amarelo-600',
    error: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cx('flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 rounded-lg border text-sm', tones[tone], className)}>
      <div className="min-w-0">{children}</div>
      {action}
    </div>
  );
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href]';

// Dialog shell: focus moves in, Tab cycles inside, Escape closes, focus
// returns to the opener when it unmounts.
export function Modal({ title, onClose, children, footer, size = 'md', closeLabel = 'Close' }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => { if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus(); };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const width = size === 'lg' ? 'max-w-2xl' : 'max-w-lg';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-900/40 p-0 sm:p-6">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-titulo"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cx('bg-white w-full rounded-t-2xl sm:rounded-xl shadow-pop max-h-[92vh] sm:max-h-[88vh] flex flex-col focus:outline-none', width)}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <h2 id="modal-titulo" className="text-base font-semibold text-gray-900">{title}</h2>
          <IconButton icon={X} label={closeLabel} onClick={onClose} />
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/60 rounded-b-xl">{footer}</div>}
      </div>
    </div>
  );
}

// Small popover menu anchored to its trigger; closes on outside click/Escape.
export function Menu({ trigger, items, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      {trigger({ open, toggle: () => setOpen(o => !o) })}
      {open && (
        <div role="menu" className={cx('absolute z-40 mt-1 min-w-[12rem] bg-white border border-gray-200 rounded-lg shadow-pop py-1', align === 'right' ? 'right-0' : 'left-0', 'bottom-full mb-2')}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); item.onClick(); }}
              className={cx('w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50', item.tone === 'danger' ? 'text-red-600' : 'text-gray-700')}
            >
              {item.icon && <item.icon size={15} />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
